import { useMemo, useCallback, useState, useEffect } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import {
  useStreamingListings,
  useStreamingFilterOptions,
  type StreamingListingsFilters,
  type StreamingListingRow,
} from '@/hooks/useStreamingListings'
import { useListingsShowSignedProgramsFlag } from '@/hooks/useListingsShowSignedProgramsFlag'
import { useEnabledLanguages } from '@/hooks/useEnabledLanguages'
import { useStreamingServiceDisplay } from '@/hooks/useStreamingServiceDisplay'
import { useAuth } from '@/contexts/AuthContext'
import { useAdmin } from '@/hooks/useAdmin'
import { useQueryClient } from '@tanstack/react-query'
import { Clapperboard } from 'lucide-react'
import TvListingImage from '@/components/TvListingImage'
import { supabase } from '@/lib/supabase'
import { STREAMING_SERVICE_CARDS } from '@/config/streamingServiceCards'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL

const CONTENT_TYPE_LABELS: Record<string, string> = {
  movie: 'Movie',
  series: 'Series',
  episode: 'Episode',
  sport_event: 'Sport',
}

const ACCESS_MODEL_LABELS: Record<string, string> = {
  included_with_subscription: 'Included with subscription',
  free_ad_supported: 'Free with ads',
  rent: 'Rent',
  buy: 'Buy',
  add_on_subscription: 'Add-on subscription',
}

function labelAccessModel(value: string): string {
  return ACCESS_MODEL_LABELS[value] ?? value
}

const POSTER_WIDTH = 90
const POSTER_HEIGHT = 130

function ListingCard({ row, onOpenDetails, showService }: { row: StreamingListingRow; onOpenDetails: (row: StreamingListingRow) => void; showService?: boolean }) {
  const watchUrl = row.deeplink_url ?? null
  const contentLabel = CONTENT_TYPE_LABELS[row.content_type] ?? row.content_type
  const metaLine = [contentLabel, row.release_year != null ? String(row.release_year) : null].filter(Boolean).join(' · ')

  return (
    <>
      <article
        style={{
          flex: 1,
          minWidth: 0,
          borderRadius: '0.75rem',
          border: '2px solid var(--bg-tertiary)',
          padding: '0.75rem',
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) auto',
          gap: '0.75rem',
          backgroundColor: 'var(--bg-secondary)',
          alignItems: 'start',
        }}
        aria-labelledby={`streaming-title-${row.id}`}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', minWidth: 0 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem' }}>
            <h2
              id={`streaming-title-${row.id}`}
              style={{ fontSize: '1rem', margin: 0, color: 'var(--text-primary)' }}
            >
              {row.title}
            </h2>
            {(row.audio_description_available === true || row.subtitles_available === true || row.sign_language_available === true) && (
              <span className="flex flex-wrap items-center gap-1.5" style={{ flexShrink: 0 }} aria-label="Accessibility options">
                {row.audio_description_available === true && (
                  <span className="text-xs font-medium px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}>AD</span>
                )}
                {row.subtitles_available === true && (
                  <span className="text-xs font-medium px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}>Subtitles</span>
                )}
                {row.sign_language_available === true && (
                  <span className="text-xs font-medium px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}>BSL</span>
                )}
              </span>
            )}
          </div>
          {metaLine && (
            <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              {metaLine}
            </p>
          )}
          {showService && row.service && (() => {
            const serviceCard = STREAMING_SERVICE_CARDS.find((c) => c.id === row.service)
            const logoUrl = serviceCard?.logoUrl ?? null
            return (
              <div className="flex items-center" style={{ marginTop: 2 }} aria-label={`Streaming service: ${row.service}`}>
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt=""
                    className="h-8 w-auto max-w-[140px] object-contain object-left"
                    loading="lazy"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{row.service}</span>
                )}
              </div>
            )
          })()}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
          <div style={{ width: POSTER_WIDTH, height: POSTER_HEIGHT, borderRadius: '0.5rem', overflow: 'hidden', backgroundColor: 'var(--bg-tertiary)' }} aria-hidden>
            <TvListingImage
              src={row.poster_url}
              alt=""
              width={POSTER_WIDTH}
              height={POSTER_HEIGHT}
              objectFit="cover"
            />
          </div>
        </div>
      </article>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
        <button
          type="button"
          onClick={() => onOpenDetails(row)}
          aria-label={`View details for ${row.title}`}
          className="px-3 py-2 text-sm font-medium rounded-lg border"
          style={{
            backgroundColor: 'transparent',
            color: 'var(--text-primary)',
            borderColor: 'var(--bg-tertiary)',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)' }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent' }}
        >
          Details
        </button>
        {watchUrl && (
          <a
            href={watchUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border"
            style={{
              backgroundColor: 'transparent',
              color: 'var(--text-primary)',
              borderColor: 'var(--bg-tertiary)',
            }}
            aria-label={`Watch ${row.title} on ${row.service}`}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)' }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent' }}
          >
            <Clapperboard size={18} aria-hidden />
            Watch
          </a>
        )}
      </div>
    </>
  )
}

function DetailsModal({ row, onClose }: { row: StreamingListingRow; onClose: () => void }) {
  const contentLabel = CONTENT_TYPE_LABELS[row.content_type] ?? row.content_type
  const watchUrl = row.deeplink_url ?? null

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="streaming-details-modal-title"
    >
      <div
        className="rounded-lg p-6 max-w-md w-full shadow-lg"
        style={{
          backgroundColor: 'var(--bg-secondary)',
          border: '2px solid var(--bg-tertiary)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-4">
          <h2 id="streaming-details-modal-title" className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
            Details
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 px-2 py-1 rounded font-medium focus:outline-none focus:ring-2 focus:ring-offset-2"
            style={{ color: 'var(--text-secondary)', backgroundColor: 'var(--bg-tertiary)' }}
            aria-label="Close modal"
          >
            Close
          </button>
        </div>
        <p className="text-sm font-medium mb-3" style={{ color: 'var(--text-primary)' }}>
          {row.title}
        </p>
        {row.description && (
          <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>
            {row.description}
          </p>
        )}
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
          <dt style={{ color: 'var(--text-secondary)' }}>Type</dt>
          <dd style={{ color: 'var(--text-primary)' }}>{contentLabel}</dd>
          {row.release_year != null && (
            <>
              <dt style={{ color: 'var(--text-secondary)' }}>Year</dt>
              <dd style={{ color: 'var(--text-primary)' }}>{row.release_year}</dd>
            </>
          )}
          <dt style={{ color: 'var(--text-secondary)' }}>Service</dt>
          <dd style={{ color: 'var(--text-primary)' }}>{row.service}</dd>
          <dt style={{ color: 'var(--text-secondary)' }}>Access</dt>
          <dd style={{ color: 'var(--text-primary)' }}>
            {row.access_models?.length
              ? row.access_models.map((a) => labelAccessModel(a)).join(' · ')
              : labelAccessModel(row.access_model)}
          </dd>
        </dl>
        {watchUrl && (
          <p className="mt-4 pt-3 border-t" style={{ borderColor: 'var(--bg-tertiary)' }}>
            <a
              href={watchUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 font-medium underline focus:outline-none focus:ring-2 focus:ring-offset-2 rounded"
              style={{ color: 'var(--color-focus)' }}
            >
              <Clapperboard className="shrink-0" size={18} aria-hidden />
              Watch on {row.service}
            </a>
          </p>
        )}
      </div>
    </div>
  )
}

function StreamingPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [detailsModalRow, setDetailsModalRow] = useState<StreamingListingRow | null>(null)
  const { session } = useAuth()
  const isAdmin = useAdmin()
  const queryClient = useQueryClient()

  const { showSignedInListings } = useListingsShowSignedProgramsFlag()
  const { enabledLanguages } = useEnabledLanguages()

  const filters: StreamingListingsFilters = useMemo(() => {
    const q = searchParams.get('q') ?? undefined
    const start = searchParams.get('start') ?? undefined
    const service = searchParams.get('service') ?? undefined
    const content_type = searchParams.get('type') ?? undefined
    const genre = searchParams.get('genre') ?? undefined
    const access = searchParams.get('access') ?? undefined
    const free = searchParams.get('free') === '1'
    const ad = searchParams.get('ad') === '1'
    const bsl = searchParams.get('bsl') === '1'
    const freeOnly = free || access === 'free'
    return {
      title_search: q?.trim() || undefined,
      title_starts_with: start && ['#', '0-9', ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')].includes(start) ? start : undefined,
      service: service || undefined,
      content_type: content_type || undefined,
      primary_genre: genre || undefined,
      access_model: access && access !== 'free' ? access : undefined,
      free_only: freeOnly ? true : undefined,
      audio_description_available: ad || undefined,
      sign_language_available: showSignedInListings && bsl ? true : undefined,
    }
  }, [searchParams, showSignedInListings])

  const page = Math.max(1, parseInt(searchParams.get('page') ?? '1', 10) || 1)

  const { data: options } = useStreamingFilterOptions()
  const { data, isLoading, error } = useStreamingListings(filters, page, enabledLanguages, {
    refetchIntervalMs: 15_000, // Poll so images update after enrichment even if Realtime doesn't fire
  })

  const selectedService = searchParams.get('service') ?? ''
  const selectedStart = searchParams.get('start') ?? ''
  const { displayedServiceIds } = useStreamingServiceDisplay()
  const visibleServiceCards = useMemo(() => {
    if (displayedServiceIds === null) return STREAMING_SERVICE_CARDS
    const set = new Set(displayedServiceIds)
    return STREAMING_SERVICE_CARDS.filter((c) => c.id === '' || set.has(c.id))
  }, [displayedServiceIds])

  const updateFilters = useCallback((updates: Record<string, string | undefined>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (updates.page !== undefined) {
        if (updates.page) next.set('page', updates.page)
        else next.delete('page')
      } else if (Object.keys(updates).some((k) => k !== 'page')) {
        next.delete('page')
      }
      if ('q' in updates) (updates.q ? next.set('q', updates.q) : next.delete('q'))
      if ('service' in updates) (updates.service ? next.set('service', updates.service) : next.delete('service'))
      if ('type' in updates) (updates.type ? next.set('type', updates.type) : next.delete('type'))
      if ('genre' in updates) (updates.genre ? next.set('genre', updates.genre) : next.delete('genre'))
      if ('access' in updates) (updates.access ? next.set('access', updates.access) : next.delete('access'))
      if ('free' in updates) (updates.free === '1' ? next.set('free', '1') : next.delete('free'))
      if ('ad' in updates) (updates.ad === '1' ? next.set('ad', '1') : next.delete('ad'))
      if ('subs' in updates) (updates.subs === '1' ? next.set('subs', '1') : next.delete('subs'))
      if ('bsl' in updates) (updates.bsl === '1' ? next.set('bsl', '1') : next.delete('bsl'))
      if ('start' in updates) (updates.start ? next.set('start', updates.start) : next.delete('start'))
      return next
    })
  }, [setSearchParams])

  const totalPages = data ? Math.ceil(data.total / data.pageSize) : 0
  const hasResults = data && data.rows.length > 0
  const isEmpty = data && data.rows.length === 0 && data.total === 0

  useEffect(() => {
    setDetailsModalRow(null)
  }, [searchParams])

  useEffect(() => {
    if (displayedServiceIds === null || !selectedService) return
    if (!displayedServiceIds.includes(selectedService)) {
      updateFilters({ service: undefined })
    }
  }, [displayedServiceIds, selectedService, updateFilters])

  // When listings are loaded, enrich only the rows currently on screen (so images appear for this page).
  useEffect(() => {
    if (!isAdmin || !session?.access_token || !supabaseUrl || !data?.rows?.length) return
    const ids = data.rows.map((r) => r.id)
    const url = `${supabaseUrl}/functions/v1/enrich_streaming_tmdb`
    console.log('[enrich_streaming_tmdb] called from Streaming page', { ids, count: ids.length })
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ ids }),
    })
      .then((res) => {
        if (res.ok) queryClient.invalidateQueries({ queryKey: ['streaming-listings'] })
      })
      .catch(() => {})
  }, [isAdmin, session?.access_token, queryClient, data])

  // Real-time: when streaming_listings rows are updated (e.g. TMDB enrichment), refetch so images appear without refresh.
  // Requires streaming_listings in supabase_realtime publication: ALTER PUBLICATION supabase_realtime ADD TABLE streaming_listings;
  useEffect(() => {
    const channel = supabase
      .channel('streaming-listings-changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'streaming_listings' },
        () => {
          queryClient.invalidateQueries({ queryKey: ['streaming-listings'] })
        }
      )
      .subscribe()
    return () => {
      supabase.removeChannel(channel)
    }
  }, [queryClient])

  const TITLE_START_OPTIONS = ['#', '0-9', ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')] as const

  return (
    <div className="max-w-6xl mx-auto px-4 py-6" aria-label="Streaming catalogue">
      {/* Accessibility filter: above the service card, same as TV Guide */}
      <div
        className="flex flex-wrap items-center justify-center gap-3 mb-4 px-1"
        role="group"
        aria-label="Accessibility filters"
      >
        <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
          Accessibility Filters:
        </span>
        <button
          type="button"
          onClick={() => updateFilters({ ad: searchParams.get('ad') === '1' ? '' : '1' })}
          className="px-4 py-2 text-sm font-medium rounded-lg border transition-colors"
          style={{
            backgroundColor: searchParams.get('ad') === '1' ? 'var(--bg-interactive)' : 'var(--bg-secondary)',
            color: searchParams.get('ad') === '1' ? 'var(--text-inverse)' : 'var(--text-primary)',
            borderColor: 'var(--bg-tertiary)',
          }}
          aria-pressed={searchParams.get('ad') === '1'}
          aria-label="Filter by AD (audio description)"
        >
          AD
        </button>
        {showSignedInListings && (
          <button
            type="button"
            onClick={() => updateFilters({ bsl: searchParams.get('bsl') === '1' ? undefined : '1' })}
            className="px-4 py-2 text-sm font-medium rounded-lg border transition-colors"
            style={{
              backgroundColor: searchParams.get('bsl') === '1' ? 'var(--bg-interactive)' : 'var(--bg-secondary)',
              color: searchParams.get('bsl') === '1' ? 'var(--text-inverse)' : 'var(--text-primary)',
              borderColor: 'var(--bg-tertiary)',
            }}
            aria-pressed={searchParams.get('bsl') === '1'}
            aria-label="Filter by BSL (British Sign Language)"
          >
            BSL
          </button>
        )}
      </div>
      <div
        className="grid grid-cols-1 gap-4 min-h-[calc(100vh-200px)] lg:grid-cols-[280px_1fr]"
      >
        {/* Services and filters sidebar — same structure as TV Guide channels */}
        <aside
          style={{
            borderRight: '2px solid var(--bg-tertiary)',
            overflowY: 'auto',
            padding: '1rem',
            backgroundColor: 'var(--bg-secondary)',
          }}
          aria-label="Services and filters"
        >
          <label htmlFor="streaming-filter-search" className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
            Search by title
          </label>
          <input
            id="streaming-filter-search"
            type="search"
            value={searchParams.get('q') ?? ''}
            onChange={(e) => updateFilters({ q: e.target.value || undefined })}
            placeholder="e.g. Breaking Bad"
            className="w-full min-h-[44px] px-3 rounded border-2 text-sm"
            style={{ borderColor: 'var(--bg-tertiary)', color: 'var(--text-primary)', backgroundColor: 'var(--bg-primary)', marginBottom: '1rem' }}
            aria-label="Search streaming titles"
          />
          <div className="mb-4" role="group" aria-label="Filter by first letter of title">
            <p className="text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
              Title starts with
            </p>
            <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(6, minmax(0, 1fr))' }}>
              {TITLE_START_OPTIONS.map((option) => {
                const isActive = selectedStart === option
                const label = option === '0-9' ? '0..' : option
                return (
                  <button
                    key={option}
                    type="button"
                    onClick={() => updateFilters({ start: isActive ? undefined : option })}
                    className="min-w-0 px-2 py-1.5 text-sm font-medium rounded-lg border transition-colors"
                    style={{
                      backgroundColor: isActive ? 'var(--bg-interactive)' : 'var(--bg-primary)',
                      color: isActive ? 'var(--text-inverse)' : 'var(--text-primary)',
                      borderColor: 'var(--bg-tertiary)',
                    }}
                    aria-pressed={isActive}
                    aria-label={option === '#' ? 'Titles starting with a special character' : option === '0-9' ? 'Titles starting with a number' : `Titles starting with ${option}`}
                  >
                    {label}
                  </button>
                )
              })}
            </div>
          </div>
          <h2 style={{ fontSize: '1.25rem', marginBottom: '0.75rem', color: 'var(--text-primary)' }}>
            Services
          </h2>
          <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
            {visibleServiceCards.map((card) => {
              const isActive = selectedService === card.id
              return (
                <button
                  key={card.id || 'all'}
                  type="button"
                  onClick={() => updateFilters({ service: isActive ? undefined : card.id })}
                  className="flex flex-col items-center justify-center rounded-lg border-2 overflow-hidden transition-colors"
                  style={{
                    backgroundColor: isActive ? 'var(--bg-interactive)' : 'var(--bg-primary)',
                    borderColor: isActive ? 'var(--bg-interactive)' : 'var(--bg-tertiary)',
                    color: isActive ? 'var(--text-inverse)' : 'var(--text-primary)',
                    minHeight: 56,
                    ...(card.logoUrl ? { height: 72 } : {}),
                  }}
                  aria-pressed={isActive}
                  aria-label={card.id ? `Filter by ${card.name}` : 'Show all services'}
                >
                  {card.logoUrl ? (
                    <span
                      className="block w-full h-full flex items-center justify-center relative"
                      style={{
                        height: '100%',
                        backgroundColor: card.logoBg === 'white' ? '#fff' : card.logoBg === 'dark' ? '#374151' : undefined,
                      }}
                    >
                      <img
                        src={card.logoUrl}
                        alt=""
                        className="max-w-full max-h-full w-auto h-auto object-contain"
                        style={{ position: 'relative', zIndex: 1 }}
                        loading="lazy"
                        referrerPolicy="no-referrer"
                      />
                    </span>
                  ) : (
                    <span className="text-sm font-medium py-3 px-2">{card.name}</span>
                  )}
                </button>
              )
            })}
          </div>
          <div className="mt-4 pt-4" style={{ borderTop: '1px solid var(--bg-tertiary)' }}>
            <span className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>Type</span>
            <div role="group" aria-label="Movie or Series" className="flex gap-1 mb-3">
              {(['', 'movie', 'series'] as const).map((value) => {
                const label = value === '' ? 'All' : value === 'movie' ? 'Movie' : 'Series'
                const isActive = (searchParams.get('type') ?? '') === value
                return (
                  <button
                    key={value || 'all'}
                    type="button"
                    onClick={() => updateFilters({ type: value || undefined })}
                    aria-pressed={isActive}
                    className="flex-1 px-3 py-2 text-sm font-medium rounded-lg border"
                    style={{
                      backgroundColor: isActive ? 'var(--bg-interactive)' : 'var(--bg-primary)',
                      color: isActive ? 'var(--text-inverse)' : 'var(--text-primary)',
                      borderColor: 'var(--bg-tertiary)',
                    }}
                  >
                    {label}
                  </button>
                )
              })}
            </div>
            <label htmlFor="streaming-filter-genre" className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Genre</label>
            <select
              id="streaming-filter-genre"
              value={searchParams.get('genre') ?? ''}
              onChange={(e) => updateFilters({ genre: e.target.value || undefined })}
              className="w-full min-h-[44px] px-3 rounded border-2 text-sm mb-3"
              style={{ borderColor: 'var(--bg-tertiary)', color: 'var(--text-primary)', backgroundColor: 'var(--bg-primary)' }}
            >
              <option value="">All genres</option>
              {(options?.genres ?? []).map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
            <label htmlFor="streaming-filter-access" className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Access</label>
            <select
              id="streaming-filter-access"
              value={searchParams.get('access') ?? ''}
              onChange={(e) => updateFilters({ access: e.target.value || undefined })}
              className="w-full min-h-[44px] px-3 rounded border-2 text-sm mb-3"
              style={{ borderColor: 'var(--bg-tertiary)', color: 'var(--text-primary)', backgroundColor: 'var(--bg-primary)' }}
            >
              <option value="">All</option>
              <option value="free">Free</option>
              {(options?.access_models ?? []).map((a) => (
                <option key={a} value={a}>{labelAccessModel(a)}</option>
              ))}
            </select>
            <label className="flex items-center gap-2 cursor-pointer" style={{ color: 'var(--text-primary)', fontSize: '0.9rem' }}>
              <input
                type="checkbox"
                checked={searchParams.get('free') === '1'}
                onChange={(e) => updateFilters({ free: e.target.checked ? '1' : undefined })}
                className="w-4 h-4"
              />
              <span>Free only</span>
            </label>
          </div>
          <p className="mt-3">
            <a href="#streaming-results" className="text-sm underline" style={{ color: 'var(--color-focus)' }}>
              Skip to results
            </a>
          </p>
        </aside>

        <main id="streaming-results" style={{ overflowY: 'auto', padding: '1rem' }} tabIndex={-1} aria-labelledby="streaming-main-heading">
          <header style={{ marginBottom: '1rem' }}>
            <h1 id="streaming-main-heading" style={{ fontSize: '1.5rem', margin: 0, color: 'var(--text-primary)' }}>
              {selectedService || 'Streaming'}
            </h1>
            <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
              {selectedService ? `Titles on ${selectedService}` : 'Catalogue — filter by service in the sidebar'}
            </p>
          </header>

          {isLoading && (
            <p role="status" aria-live="polite" style={{ color: 'var(--text-secondary)' }}>
              Loading…
            </p>
          )}
          {error && (
            <p role="alert" style={{ color: 'var(--color-error)' }}>
              Failed to load streaming catalogue: {error instanceof Error ? error.message : String(error)}.
            </p>
          )}
          {!isLoading && !error && isEmpty && (
            <section aria-label="No results" className="rounded-lg border-2 p-6 text-center" style={{ borderColor: 'var(--bg-tertiary)' }}>
              <p className="mb-2" style={{ color: 'var(--text-primary)' }}>
                No titles match your filters, or the catalogue is empty.
              </p>
              <p style={{ color: 'var(--text-secondary)' }}>
                Try changing filters or <Link to="/tv-guide" className="underline" style={{ color: 'var(--color-focus)' }}>open the TV Guide</Link> for schedule-based listings. <Link to="/help/welcome#help-streaming" className="underline" style={{ color: 'var(--color-focus)' }}>Help: Streaming</Link>.
              </p>
            </section>
          )}
          {!isLoading && !error && hasResults && (
            <>
              <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
                {data!.total} title{data!.total !== 1 ? 's' : ''} found.
              </p>
              <ul
                style={{
                  listStyle: 'none',
                  margin: 0,
                  padding: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.75rem',
                  width: '100%',
                }}
                role="list"
              >
                {data!.rows.map((row) => (
                  <li
                    key={row.id}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}
                  >
                    <ListingCard row={row} onOpenDetails={setDetailsModalRow} showService={!selectedService} />
                  </li>
                ))}
              </ul>
              {detailsModalRow && (
                <DetailsModal row={detailsModalRow} onClose={() => setDetailsModalRow(null)} />
              )}
              {totalPages > 1 && (
                <nav className="mt-6 flex flex-wrap gap-2" aria-label="Pagination">
                  {page > 1 && (
                    <button
                      type="button"
                      onClick={() => updateFilters({ page: String(page - 1) })}
                      className="px-4 py-2 rounded border-2 font-medium"
                      style={{ borderColor: 'var(--bg-tertiary)', color: 'var(--text-primary)', backgroundColor: 'var(--bg-secondary)' }}
                    >
                      Previous
                    </button>
                  )}
                  <span className="px-4 py-2" style={{ color: 'var(--text-secondary)' }}>
                    Page {page} of {totalPages}
                  </span>
                  {page < totalPages && (
                    <button
                      type="button"
                      onClick={() => updateFilters({ page: String(page + 1) })}
                      className="px-4 py-2 rounded border-2 font-medium"
                      style={{ borderColor: 'var(--bg-tertiary)', color: 'var(--text-primary)', backgroundColor: 'var(--bg-secondary)' }}
                    >
                      Next
                    </button>
                  )}
                </nav>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  )
}

export default StreamingPage
